import { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { SubscriptionPlan } from '@/types/saas';

export default function Pricing() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      setPlans((data || []) as SubscriptionPlan[]);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (!user) {
      navigate('/signup', { state: { planId: plan.id } });
    } else {
      navigate('/tenant-signup', { state: { planId: plan.id } });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">جاري تحميل الخطط...</p>
        </div>
      </div>
    );
  }

  const monthlyPlans = plans.filter(p => p.type === 'monthly');
  const yearlyPlans = plans.filter(p => p.type === 'yearly');

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">اختر الخطة المناسبة لك</h1>
          <p className="text-xl text-muted-foreground">
            ابدأ بتجربة مجانية لمدة 14 يوم بدون الحاجة لبطاقة ائتمان
          </p>
        </div>

        {/* Monthly Plans */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-center mb-8">الخطط الشهرية</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {monthlyPlans.map((plan) => (
              <Card key={plan.id} className={plan.name === 'Professional' ? 'border-primary shadow-lg' : ''}>
                <CardHeader>
                  {plan.name === 'Professional' && (
                    <Badge className="w-fit mb-2">الأكثر شعبية</Badge>
                  )}
                  <CardTitle>{plan.name_ar}</CardTitle>
                  <CardDescription>{plan.name}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground mr-2">ريال/شهر</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                    {plan.max_users > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">حتى {plan.max_users} مستخدمين</span>
                      </li>
                    )}
                    {plan.max_users === -1 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">مستخدمين غير محدودين</span>
                      </li>
                    )}
                    {plan.max_bookings_per_month > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">حتى {plan.max_bookings_per_month} حجز شهرياً</span>
                      </li>
                    )}
                    {plan.max_bookings_per_month === -1 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">حجوزات غير محدودة</span>
                      </li>
                    )}
                  </ul>
                  <Button 
                    className="w-full mt-6" 
                    variant={plan.name === 'Professional' ? 'default' : 'outline'}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    ابدأ التجربة المجانية
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Yearly Plans */}
        <div>
          <h2 className="text-2xl font-semibold text-center mb-8">الخطط السنوية</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {yearlyPlans.map((plan) => (
              <Card key={plan.id} className="relative">
                <Badge className="absolute -top-3 right-4 bg-green-500">وفر 17%</Badge>
                <CardHeader>
                  <CardTitle>{plan.name_ar}</CardTitle>
                  <CardDescription>{plan.name}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground mr-2">ريال/سنة</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                    {plan.max_users > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">حتى {plan.max_users} مستخدمين</span>
                      </li>
                    )}
                    {plan.max_users === -1 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">مستخدمين غير محدودين</span>
                      </li>
                    )}
                    {plan.max_bookings_per_month > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">حتى {plan.max_bookings_per_month} حجز شهرياً</span>
                      </li>
                    )}
                    {plan.max_bookings_per_month === -1 && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">حجوزات غير محدودة</span>
                      </li>
                    )}
                  </ul>
                  <Button 
                    className="w-full mt-6" 
                    variant="outline"
                    onClick={() => handleSelectPlan(plan)}
                  >
                    ابدأ التجربة المجانية
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}